import React, { useState } from 'react';
import toast from 'react-hot-toast';
import CohortPicker from './CohortPicker';
import buildJobsPdf from './buildJobsPdf';
import {
  downloadJobsCsv,
  jobsError,
  useCampaigns,
  useJobCohorts,
  useReportSummary,
} from '../../hooks/useJobDigest';
import {
  Card,
  count,
  date,
  Empty,
  INPUT,
  percent,
  SECONDARY,
  Stat,
} from './ui';

export default function ReportsTab({
  cohortIds,
  setCohortIds,
  campaignId,
  setCampaignId,
}) {
  const [busy, setBusy] = useState('');
  const { data: campaigns = [] } = useCampaigns();
  const { data: cohorts = [] } = useJobCohorts();
  const filters = { cohortIds, campaignId };
  const { data, isLoading, error } = useReportSummary(filters);
  const t = data?.total;

  const csv = async (report) => {
    setBusy(report);
    try {
      await downloadJobsCsv(report, filters);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy('');
    }
  };

  const pdf = () => {
    const names = cohortIds.map(
      (id) => cohorts.find((c) => c.id === id)?.name || id
    );
    const campaign = campaigns.find((c) => c.id === campaignId);
    buildJobsPdf(data, {
      cohortNames: names,
      campaignName: campaign?.name,
    }).save(`job-outcomes-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-5">
      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <CohortPicker value={cohortIds} onChange={setCohortIds} />
          </div>
          <select
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
            className={INPUT}
          >
            <option value="">All campaigns</option>
            {campaigns
              .filter((c) => !c.dry_run)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
      </Card>

      {!cohortIds.length && (
        <Empty>Choose one or more cohorts to see their report.</Empty>
      )}
      {isLoading && cohortIds.length > 0 && (
        <p className="text-sm text-gray-500">Loading…</p>
      )}
      {error && (
        <p className="text-sm text-red-700">
          {jobsError(error, "Couldn't load the report.")}
        </p>
      )}

      {t && (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <Stat
              label="Graduates emailed"
              value={count(t.graduates_emailed)}
              hint={`of ${count(t.certified_graduates)} certified · ${count(
                t.jobs_sent
              )} jobs sent`}
            />
            <Stat
              label="Opened a job"
              value={percent(t.click_rate)}
              hint={`${count(t.graduates_clicked)} graduates`}
            />
            <Stat
              label="Applied (proof approved)"
              value={percent(t.apply_rate)}
              hint={`${count(t.graduates_applied)} graduates · ${count(
                t.proofs_pending
              )} proofs waiting`}
            />
            <Stat
              label="Got a job"
              value={percent(t.hire_rate)}
              hint={`${count(t.graduates_hired)} graduates, ${count(
                t.graduates_hired_via_digest
              )} from our emails`}
            />
          </div>

          <Card
            title="By cohort"
            aside={
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className={SECONDARY}
                  disabled={Boolean(busy)}
                  onClick={() => csv('students')}
                >
                  {busy === 'students' ? 'Preparing…' : 'Students (CSV)'}
                </button>
                <button
                  type="button"
                  className={SECONDARY}
                  disabled={Boolean(busy)}
                  onClick={() => csv('recommendations')}
                >
                  {busy === 'recommendations'
                    ? 'Preparing…'
                    : 'Every job sent (CSV)'}
                </button>
                <button type="button" className={SECONDARY} onClick={pdf}>
                  Partner report (PDF)
                </button>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Cohort</th>
                    <th className="py-2 pr-3 text-right">Certified</th>
                    <th className="py-2 pr-3 text-right">Emailed</th>
                    <th className="py-2 pr-3 text-right">Opened</th>
                    <th className="py-2 pr-3 text-right">Applied</th>
                    <th className="py-2 pr-3 text-right">Proofs waiting</th>
                    <th className="py-2 text-right">Got a job</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.map((c) => (
                    <tr key={c.cohort_id} className="border-b last:border-0">
                      <td className="py-2 pr-3">{c.cohort || c.cohort_id}</td>
                      <td className="py-2 pr-3 text-right">
                        {count(c.certified_graduates)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {count(c.graduates_emailed)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {percent(c.click_rate)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {count(c.graduates_applied)} ({percent(c.apply_rate)})
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {count(c.proofs_pending)}
                      </td>
                      <td className="py-2 text-right">
                        {count(c.graduates_hired)} ({percent(c.hire_rate)})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-600">
              Only emails actually sent count; dry runs and test emails are left
              out. &ldquo;Applied&rdquo; counts graduates with at least one
              proof approved by PLP staff. &ldquo;Got a job&rdquo; is what
              graduates told us. Generated {date(data.generated_at, true)}.
            </p>
          </Card>

          {data.top_applied_jobs?.length > 0 && (
            <Card title="Roles graduates applied for most">
              <ul className="divide-y divide-gray-100 text-sm">
                {data.top_applied_jobs.map((job) => (
                  <li
                    key={`${job.title}-${job.company}`}
                    className="flex justify-between py-1.5"
                  >
                    <span>
                      {job.title}
                      {job.company && (
                        <span className="text-gray-500"> · {job.company}</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {count(job.applications)} applications
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
